import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import AddUserModal from './components/AddUserModal';
import EditUserModal from './components/EditUserModal';
import DeleteConfirmModal from '@/components/base/DeleteConfirmModal';

import { getFileUrl } from '@/utils/fileUrl';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string | null;
  role: string;
  role_id: number;
  is_active: number;
  is_deleted: number;
  deleted_at: string | null;
  last_login: string | null;
  created_at: string;
  avatar_url: string | null;
}

interface ManagedCounts {
  companies: number;
  customers: number;
}

export default function Users() {
  const { put, del, get } = useApi();
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; mode: 'soft' | 'hard' } | null>(null);
  const [toggleLoadingId, setToggleLoadingId] = useState<number | null>(null);

  // Reassign before delete flow
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState<User | null>(null);
  const [managedCounts, setManagedCounts] = useState<ManagedCounts | null>(null);
  const [newManagerId, setNewManagerId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);

  const { data: users, loading, refetch } = useApiQuery<User[]>({
    endpoint: `/users${showDeleted ? '?showDeleted=true' : ''}`,
  });

  // Fetch all users for the reassign dropdown (exclude the one being deleted)
  const { data: allUsersData } = useApiQuery<User[]>({
    endpoint: '/users',
    enabled: reassignModalOpen,
  });
  const availableManagers = (allUsersData || []).filter((u) => u.id !== reassignTarget?.id && !u.is_deleted);

  const filtered = users?.filter((u) =>
    (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const roleColors: Record<string, string> = {
    superadmin: 'bg-violet-50 text-violet-700',
    company_admin: 'bg-brand-50 text-brand-700',
    kyc_officer: 'bg-sky-50 text-sky-700',
    customer: 'bg-stone-100 text-stone-700',
  };

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'soft') return;
    await put(`/users/${deleteTarget.id}/soft-delete`, {});
    setDeleteTarget(null);
    refetch();
  }, [deleteTarget, put, refetch]);

  const handleHardDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'hard') return;
    await del(`/users/${deleteTarget.id}`);
    setDeleteTarget(null);
    refetch();
  }, [deleteTarget, del, refetch]);

  const handleRestore = useCallback(async (id: number) => {
    await put(`/users/${id}/restore`, {});
    refetch();
  }, [put, refetch]);

  // Check if user manages anything before deleting
  const checkManagedBeforeDelete = useCallback(async (user: User, mode: 'soft' | 'hard') => {
    try {
      const res = await get(`/users/${user.id}/managed-counts`);
      const counts = (res as { data?: ManagedCounts })?.data;
      if (counts && (counts.companies > 0 || counts.customers > 0)) {
        setReassignTarget(user);
        setManagedCounts(counts);
        setReassignModalOpen(true);
        setNewManagerId('');
        setReassignError(null);
      } else {
        // Nothing managed, proceed straight to delete
        setDeleteTarget({ id: user.id, name: user.full_name, mode });
      }
    } catch {
      // If the endpoint fails, just proceed with delete
      setDeleteTarget({ id: user.id, name: user.full_name, mode });
    }
  }, [put]);

  const handleReassignAndDelete = useCallback(async () => {
    if (!reassignTarget || !newManagerId) return;
    setReassignLoading(true);
    setReassignError(null);

    try {
      // Reassign managed profiles to new user
      await put(`/users/${reassignTarget.id}/reassign`, { newUserId: Number(newManagerId) });
      // Then soft delete the original user
      await put(`/users/${reassignTarget.id}/soft-delete`, {});
      setReassignModalOpen(false);
      setReassignTarget(null);
      setNewManagerId('');
      refetch();
    } catch (err) {
      setReassignError(err instanceof Error ? err.message : 'Reassignment failed');
    } finally {
      setReassignLoading(false);
    }
  }, [reassignTarget, newManagerId, put, refetch]);

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Users</h1>
          <p className="text-stone-500 text-sm mt-1">Manage system users and their access levels.</p>
        </div>
        <button
          onClick={() => setAddModalOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" />
          Add User
        </button>
      </div>

      {/* Search & Filter */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative max-w-md flex-1 w-full">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm w-4 h-4 flex items-center justify-center" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users by name or email..."
            className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
          />
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          />
          <div className="w-9 h-5 bg-stone-300 peer-checked:bg-brand-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
          <span className="text-stone-600 text-sm">Show deleted</span>
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Last Login</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered?.map((user) => (
                  <tr
                    key={user.id}
                    className={`hover:bg-stone-50 transition-colors ${user.is_deleted ? 'bg-stone-50/60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${user.is_deleted ? 'bg-stone-200' : 'bg-brand-100'}`}>
                          {user.avatar_url ? (
                            <img src={getFileUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className={`font-bold text-xs ${user.is_deleted ? 'text-stone-400' : 'text-brand-700'}`}>
                              {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${user.is_deleted ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                            {user.full_name}
                          </p>
                          <p className="text-stone-400 text-xs">{user.email}</p>
                          {user.is_deleted && user.deleted_at && (
                            <p className="text-red-400 text-[10px]">Deleted {new Date(user.deleted_at).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[user.role] || 'bg-stone-100 text-stone-700'}`}>
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{user.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_deleted ? 'bg-red-50 text-red-700' :
                        user.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {user.is_deleted ? 'Deleted' : user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-sm">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {!user.is_deleted && (
                          <>
                            <button
                              onClick={() => setEditUser(user)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors cursor-pointer"
                              title="Edit"
                            >
                              <i className="ri-pencil-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                            <button
                              onClick={async () => {
                                setToggleLoadingId(user.id);
                                try {
                                  await put(`/users/${user.id}`, { isActive: !user.is_active });
                                  refetch();
                                } catch {
                                  // silently fail
                                } finally {
                                  setToggleLoadingId(null);
                                }
                              }}
                              disabled={toggleLoadingId === user.id}
                              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                user.is_active
                                  ? 'hover:bg-amber-50 text-stone-400 hover:text-amber-600'
                                  : 'hover:bg-emerald-50 text-stone-400 hover:text-emerald-600'
                              }`}
                              title={user.is_active ? 'Deactivate user' : 'Activate user'}
                            >
                              {toggleLoadingId === user.id ? (
                                <i className="ri-loader-4-line animate-spin text-xs w-4 h-4 flex items-center justify-center" />
                              ) : user.is_active ? (
                                <i className="ri-close-circle-line text-sm w-4 h-4 flex items-center justify-center" />
                              ) : (
                                <i className="ri-check-line text-sm w-4 h-4 flex items-center justify-center" />
                              )}
                            </button>
                            <button
                              onClick={() => checkManagedBeforeDelete(user, 'soft')}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                              title="Soft delete"
                            >
                              <i className="ri-delete-bin-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                          </>
                        )}
                        {user.is_deleted && (
                          <>
                            <button
                              onClick={() => handleRestore(user.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
                              title="Reactivate"
                            >
                              <i className="ri-arrow-go-back-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                            <button
                              onClick={() => checkManagedBeforeDelete(user, 'hard')}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                              title="Permanently delete"
                            >
                              <i className="ri-fire-line text-sm w-4 h-4 flex items-center justify-center" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered?.length === 0 && (
              <div className="p-8 text-center">
                <i className="ri-group-line text-stone-300 text-3xl mb-2 w-12 h-12 flex items-center justify-center mx-auto" />
                <p className="text-stone-400 text-sm">No users found.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <AddUserModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onSuccess={refetch} />
      <EditUserModal open={!!editUser} onClose={() => setEditUser(null)} onSuccess={refetch} user={editUser} />
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.mode === 'hard' ? handleHardDelete : handleSoftDelete}
        title={deleteTarget?.mode === 'hard' ? 'Permanent Delete' : 'Deactivate User'}
        entityName={deleteTarget?.name}
        description={
          deleteTarget?.mode === 'hard'
            ? 'This will permanently erase the user account and their avatar. KYC requests and documents will remain. This action cannot be undone.'
            : 'The user account will be disabled and hidden from the list. You can reactivate it later.'
        }
      />

      {/* Reassign Before Delete Modal */}
      {reassignModalOpen && reassignTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setReassignModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
              <div>
                <h2 className="text-lg font-bold text-stone-900">Reassign Before Deleting</h2>
                <p className="text-stone-400 text-xs mt-0.5">{reassignTarget.full_name} manages active profiles that need reassignment</p>
              </div>
              <button
                onClick={() => setReassignModalOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {reassignError && (
                <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
                  {reassignError}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-alert-line text-amber-600 w-4 h-4 flex items-center justify-center" />
                  <span className="text-sm font-semibold text-amber-800">Managed Profiles</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
                      {managedCounts?.companies ?? 0}
                    </span>
                    <span className="text-amber-700 text-xs">Companies</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">
                      {managedCounts?.customers ?? 0}
                    </span>
                    <span className="text-amber-700 text-xs">Customers</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Reassign To *</label>
                <select
                  value={newManagerId}
                  onChange={(e) => setNewManagerId(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Choose a new manager...</option>
                  {availableManagers.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name} ({u.role.replace('_', ' ')})</option>
                  ))}
                </select>
              </div>

              <p className="text-stone-400 text-xs leading-relaxed">
                <i className="ri-information-line text-brand-600 mr-1" />
                All company and customer profiles currently managed by {reassignTarget.full_name} will be transferred to the selected user before deletion.
              </p>

              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setReassignModalOpen(false)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReassignAndDelete}
                  disabled={!newManagerId || reassignLoading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {reassignLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Reassigning...
                    </>
                  ) : (
                    <>
                      <i className="ri-exchange-line" />
                      Reassign &amp; Deactivate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}